import React, { useState, useEffect, useRef } from 'react';
import Modal from '@/components/ui/Modal/Modal.tsx';
import styles from './TravelerDataComplianceModal.module.scss';
import FlexBlock from '@/components/ui/FlexBlock/FlexBlock.tsx';
import moment from 'moment';
import Pagination from '@/components/ui/Pagination/Pagination.tsx';
import Spinner from '@/components/ui/Spinner/Spinner.tsx';
import Tooltip from '@/components/ui/Tooltip/Tooltip.tsx';
import { useQuery } from '@tanstack/react-query';
import queryKeys from '@/constants/queryKeys.ts';
import { getSourceModelItineraries, getTravelerItineraries } from '@/api/dataCompliance.ts';
import { useModalStore } from '@/store/useModalStore.ts';
import Co2Period = App.Enums.Co2Period;
import breakpoints from '@/constants/breakpoints.ts';
import ListDataComplianceItinerariesResponse = App.Data.Response.ListDataComplianceItinerariesResponse;
import classNames from 'classnames';
import { externalLinks } from '@/constants/externalLinks.ts';
import { trackEvent } from '@/utils/productAnalyticsUtil.ts';
import { productAnalyticsEvents } from '@/constants/productAnalyticsEvents.ts';

const travelerTooltipText = 'The traveler is the person who travels.';
const bookerTooltipText = 'The travel arranger is the person who booked the trip.';
const missingDetailsTooltipText =
	'Shows whether accommodation or transport details are missing. Add the relevant details or confirm they weren’t needed.';
const tripNamePreviewLength = 20;

interface TravelerDataComplianceModalProps {
	timePeriod: Co2Period;
}

interface BookerTripsPreviewModalData {
	userId: number;
	returnToGroup?: GroupTripsPreviewModalData;
}

type GroupModalTab = 'travel-arrangers' | 'trips';

interface GroupBookerPreviewData {
	id: number;
	name: string;
	officeName: string | null;
	departmentName: string | null;
	nonCompliantItinerariesCount: number;
}

interface GroupTripsPreviewModalData {
	groupTitle: string;
	groupSubtitle?: string;
	totalMissingTrips: number;
	legalEntityId: number;
	officeId?: number;
	departmentId?: number;
	initialTab?: GroupModalTab;
	bookers: GroupBookerPreviewData[];
}

const isBookerTripsPreviewModalData = (data: unknown): data is BookerTripsPreviewModalData => {
	if (!data || typeof data !== 'object') {
		return false;
	}

	return 'userId' in data;
};

const isGroupTripsPreviewModalData = (data: unknown): data is GroupTripsPreviewModalData => {
	if (!data || typeof data !== 'object') {
		return false;
	}

	return 'groupTitle' in data && 'legalEntityId' in data && 'bookers' in data;
};

const TripCountLabel: React.FC<{ count: number }> = ({ count }) => (
	<>
		{count} {count === 1 ? 'trip' : 'trips'}
	</>
);

const TripNameWithTooltip: React.FC<{ name: string; providerRef: string }> = ({ name, providerRef }) => {
	const isTruncated = name.length > tripNamePreviewLength;
	const displayName = isTruncated ? `${name.slice(0, tripNamePreviewLength)}...` : name;
	const content = (
		<>
			{displayName}
			{isTruncated && (
				<span className={styles.tripNameTooltip} role="tooltip">
					{name}
				</span>
			)}
		</>
	);

	return (
		<a
			href={`${externalLinks.spotnana.root}/trips/${providerRef}`}
			target="_blank"
			rel="noopener noreferrer"
			className={classNames(styles.tripName, styles.tripNameLink)}
			aria-label={isTruncated ? name : undefined}
		>
			{content}
		</a>
	);
};

const TravelerDataComplianceModal: React.FC<TravelerDataComplianceModalProps> = ({ timePeriod }) => {
	const { data: modalData, openModal } = useModalStore();
	const bookerPreviewData = isBookerTripsPreviewModalData(modalData)
		? modalData
		: typeof modalData === 'number'
			? { userId: modalData }
			: undefined;
	const groupPreviewData = isGroupTripsPreviewModalData(modalData) ? modalData : undefined;
	const [isHeightCalculated, setIsHeightCalculated] = useState<boolean>(false);
	const [pagination, setPagination] = useState({
		page: 1,
		perPage: 5,
	});
	const [displayBookerData, setDisplayBookerData] = useState<ListDataComplianceItinerariesResponse>();
	const previousBookerIdRef = useRef<number | undefined>(bookerPreviewData?.userId);
	const [groupActiveTab, setGroupActiveTab] = useState<GroupModalTab>('travel-arrangers');
	const [groupTripsPage, setGroupTripsPage] = useState(1);

	const travelerItinerariesQuery = useQuery({
		queryKey: [
			queryKeys.dataComplianceTravelerItineraries,
			timePeriod,
			bookerPreviewData?.userId,
			pagination.page,
			pagination.perPage,
		],
		queryFn: async () => {
			const params = {
				period: timePeriod,
				page: pagination.page,
				perPage: pagination.perPage,
				userId: bookerPreviewData?.userId || 0,
			};
			return await getTravelerItineraries(params);
		},
		enabled: !!bookerPreviewData && isHeightCalculated,
	});

	const groupTripsQuery = useQuery({
		queryKey: [
			queryKeys.dataComplianceSourceModelItineraries,
			timePeriod,
			groupPreviewData?.legalEntityId,
			groupPreviewData?.officeId,
			groupPreviewData?.departmentId,
			groupTripsPage,
			pagination.perPage,
		],
		queryFn: async () =>
			await getSourceModelItineraries({
				period: timePeriod,
				legalEntityId: groupPreviewData?.legalEntityId ?? 0,
				officeId: groupPreviewData?.officeId,
				departmentId: groupPreviewData?.departmentId,
				page: groupTripsPage,
				perPage: pagination.perPage,
			}),
		enabled: !!groupPreviewData && groupActiveTab === 'trips' && isHeightCalculated,
		placeholderData: previousData => previousData,
	});

	const calculateVisibleRows = () => {
		const maxModalHeight = window.innerHeight * 0.9;
		const modalPadding = 80;
		const nameDescHeight = window.innerWidth < 768 ? 120 : 50;
		const totalNumsHeight = 45;
		const tableHeaderHeight = 75;
		const paginationHeight = 64;
		const tableRowHeight = 72.5;

		const fixedElementsHeight = nameDescHeight + totalNumsHeight + tableHeaderHeight + paginationHeight;
		const availableHeight = maxModalHeight - modalPadding - fixedElementsHeight;
		return Math.max(1, Math.floor(availableHeight / tableRowHeight));
	};

	useEffect(() => {
		const updateVisibleRows = () => {
			const calculatedRows = calculateVisibleRows();
			setPagination(prev => ({
				...prev,
				perPage: calculatedRows,
				page: 1,
			}));
			setIsHeightCalculated(true);
		};

		updateVisibleRows();
		window.addEventListener('resize', updateVisibleRows);
		return () => window.removeEventListener('resize', updateVisibleRows);
	}, []);

	const handlePagination = (num: number) => {
		setPagination(prev => ({
			...prev,
			page: num,
		}));
	};

	const getInitials = (name: string) => {
		if (!name) return '';
		const parts = name.trim().split(' ');
		if (parts.length === 1) return parts[0][0].toUpperCase();
		return parts[0][0].toUpperCase() + parts[parts.length - 1][0].toUpperCase();
	};

	const getCurrentPageItems = () => {
		return displayBookerData?.nonCompliantItineraries || [];
	};
	const groupTrips = groupTripsQuery.data?.nonCompliantItineraries || [];
	const groupTripsQueriesEnabled = !!groupPreviewData && groupActiveTab === 'trips' && isHeightCalculated;
	const isGroupTripsLoading = groupTripsQueriesEnabled && (groupTripsQuery.isLoading || groupTripsQuery.isFetching);
	const isGroupTripsError = groupTripsQuery.isError;

	const getTravelerName = (travelerName?: string | null) => travelerName || '–';
	const getContactValue = (user: unknown, keys: string[]) => {
		if (!user || typeof user !== 'object') {
			return undefined;
		}

		for (const key of keys) {
			if (!(key in user)) {
				continue;
			}

			const value = (user as Record<string, unknown>)[key];

			if (typeof value === 'string' && value) {
				const trimmedValue = value.trim();

				if (trimmedValue && trimmedValue !== '-' && trimmedValue !== '–') {
					return trimmedValue;
				}
			}
		}

		return undefined;
	};
	const getBookerContactDetails = (user?: unknown) => {
		return [getContactValue(user, ['email']), getContactValue(user, ['phone', 'phoneNumber'])].filter(Boolean);
	};
	const openBookerTripsPreview = (userId: number) => {
		trackEvent(productAnalyticsEvents.climatePro.dataGap.tripDataCoverage.interact, {
			deduplicationKey: `travel-arranger.${userId}.trips`,
			postHogEventName: 'data_gap_trips_clicked',
			postHogProperties: {
				category: 'data-gap',
				source: 'travel_arrangers_modal',
			},
		});
		setDisplayBookerData(undefined);
		setPagination(prev => ({
			...prev,
			page: 1,
		}));
		openModal('travelerDataCompliance', {
			userId,
			...(groupPreviewData && { returnToGroup: { ...groupPreviewData, initialTab: 'travel-arrangers' } }),
		});
	};
	const handleGroupTabChange = (tab: GroupModalTab) => {
		if (tab === groupActiveTab) {
			return;
		}

		setGroupActiveTab(tab);
		trackEvent(productAnalyticsEvents.climatePro.dataGap.tripDataCoverage.interact, {
			deduplicationKey: `modal-tab.${tab}`,
			postHogEventName:
				tab === 'travel-arrangers' ? 'data_gap_travel_arrangers_clicked' : 'data_gap_trips_clicked',
			postHogProperties: {
				category: 'data-gap',
				source: 'modal_tab',
			},
		});
	};

	const backToGroupPreview = () => {
		if (!bookerPreviewData?.returnToGroup) {
			return;
		}

		setGroupActiveTab(bookerPreviewData.returnToGroup.initialTab || 'travel-arrangers');
		setGroupTripsPage(1);
		openModal('travelerDataCompliance', bookerPreviewData.returnToGroup);
	};

	useEffect(() => {
		if (previousBookerIdRef.current === bookerPreviewData?.userId) {
			return;
		}

		previousBookerIdRef.current = bookerPreviewData?.userId;
		setDisplayBookerData(undefined);
		setPagination(prev => ({
			...prev,
			page: 1,
		}));
	}, [bookerPreviewData?.userId]);

	useEffect(() => {
		if (travelerItinerariesQuery.data) {
			setDisplayBookerData(travelerItinerariesQuery.data);
		}
	}, [travelerItinerariesQuery.data]);

	useEffect(() => {
		if (!groupPreviewData) {
			return;
		}

		setGroupActiveTab(groupPreviewData.initialTab || 'travel-arrangers');
		setGroupTripsPage(1);
	}, [groupPreviewData]);

	useEffect(() => {
		setGroupTripsPage(1);
	}, [groupActiveTab, pagination.perPage]);

	if (groupPreviewData) {
		return (
			<Modal name="travelerDataCompliance" className={styles.travelerDataComplianceModalWrapper}>
				<FlexBlock
					alignItems={{
						[breakpoints.zero]: 'flex-start',
						[breakpoints.tablet]: 'center',
					}}
					justifyContent="space-between"
					flexDirection={{
						[breakpoints.zero]: 'column',
						[breakpoints.tablet]: 'row',
					}}
					rowGap="20px"
					className={styles.nameDesc}
				>
					<FlexBlock alignItems="center">
						<span className={styles.initials}>{getInitials(groupPreviewData.groupTitle)}</span>
						<FlexBlock flexDirection="column" justifyContent="flex-start">
							<p className={styles.name}>{groupPreviewData.groupTitle}</p>
							<p className={styles.desc}>{groupPreviewData.groupSubtitle}</p>
						</FlexBlock>
					</FlexBlock>
				</FlexBlock>

				<FlexBlock columnGap="22px" alignItems="center" className={styles.totalNums}>
					<FlexBlock columnGap="10px" alignItems="center">
						<p>Missing details</p>
						<span>{groupPreviewData.totalMissingTrips}</span>
					</FlexBlock>
					<FlexBlock columnGap="10px" alignItems="center">
						<p>Travel arrangers</p>
						<span>{groupPreviewData.bookers.length}</span>
					</FlexBlock>
				</FlexBlock>

				<div className={styles.modalTabs}>
					<button
						type="button"
						className={classNames(groupActiveTab === 'travel-arrangers' && styles.active)}
						onClick={() => handleGroupTabChange('travel-arrangers')}
					>
						Travel arrangers
					</button>
					<button
						type="button"
						className={classNames(groupActiveTab === 'trips' && styles.active)}
						onClick={() => handleGroupTabChange('trips')}
					>
						Trips
					</button>
				</div>

				{groupActiveTab === 'travel-arrangers' ? (
					<div className={styles.tableScrollWrapper}>
						<table>
							<thead>
								<tr>
									<th>
										<FlexBlock alignItems="center" columnGap="2px" className={styles.filter}>
											Travel arranger{' '}
											<div className={styles.tooltip}>
												<Tooltip text={bookerTooltipText} />
											</div>
										</FlexBlock>
									</th>
									<th>Office</th>
									<th>Department</th>
									<th className={styles.alignRightHeader}>
										<FlexBlock
											alignItems="center"
											justifyContent="flex-end"
											columnGap="2px"
											className={styles.filter}
										>
											Missing details
											<div className={styles.tooltip}>
												<Tooltip text={missingDetailsTooltipText} />
											</div>
										</FlexBlock>
									</th>
								</tr>
							</thead>
							<tbody>
								{groupPreviewData.bookers.map(booker => (
									<tr key={booker.id}>
										<td>
											<span>{booker.name}</span>
										</td>
										<td>{booker.officeName || '–'}</td>
										<td>{booker.departmentName || '–'}</td>
										<td>
											<button
												type="button"
												className={styles.missingTripsButton}
												onClick={() => openBookerTripsPreview(booker.id)}
											>
												<TripCountLabel count={booker.nonCompliantItinerariesCount} />
											</button>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				) : (
					<div className={styles.tabContent}>
						{isGroupTripsError ? (
							<div className={styles.errorWrapper}>
								<p>We couldn’t load these trips. Try again in a moment.</p>
							</div>
						) : isGroupTripsLoading && !groupTrips.length ? (
							<div className={styles.spinnerWrapper}>
								<Spinner absolute />
							</div>
						) : groupTrips.length ? (
							<>
								{isGroupTripsLoading && (
									<div className={styles.fetchingOverlay}>
										<Spinner absolute className={styles.fetchingSpinner} />
									</div>
								)}
								<div className={styles.tableScrollWrapper}>
									<table>
										<thead>
											<tr>
												<th>Trip</th>
												<th>
													<FlexBlock
														alignItems="center"
														columnGap="2px"
														className={styles.filter}
													>
														Travel arranger{' '}
														<div className={styles.tooltip}>
															<Tooltip text={bookerTooltipText} />
														</div>
													</FlexBlock>
												</th>
												<th>
													<FlexBlock
														alignItems="center"
														columnGap="2px"
														className={styles.filter}
													>
														Traveler{' '}
														<div className={styles.tooltip}>
															<Tooltip text={travelerTooltipText} />
														</div>
													</FlexBlock>
												</th>
												<th className={styles.alignRightHeader}>
													<FlexBlock
														alignItems="center"
														columnGap="2px"
														className={styles.filter}
													>
														Missing details
														<div className={styles.tooltip}>
															<Tooltip text={missingDetailsTooltipText} />
														</div>
													</FlexBlock>
												</th>
											</tr>
										</thead>
										<tbody>
											{groupTrips.map((itinerary, index) => (
												<tr
													key={`${itinerary.id}-${itinerary.bookerId}-${itinerary.travelerId}-${index}`}
												>
													<td>
														<FlexBlock
															alignItems="center"
															columnGap="4px"
															className={styles.destination}
														>
															<FlexBlock flexDirection="column" rowGap="2px">
																<p className={styles.city}>
																	<TripNameWithTooltip
																		name={itinerary.tripName}
																		providerRef={itinerary.providerRef}
																	/>
																</p>
																<p className={styles.date}>
																	{`${moment(itinerary.startDate).format('DD.MM.YY')} – ${moment(itinerary.endDate).format('DD.MM.YY')}`}
																</p>
															</FlexBlock>
														</FlexBlock>
													</td>
													<td>{itinerary.bookerName || '–'}</td>
													<td>{getTravelerName(itinerary.travelerName)}</td>
													<td>
														<span className={styles.complianceFlag}>
															{itinerary.complianceGapType === 'missing-hotel'
																? 'Accommodation'
																: 'Transport'}
														</span>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
								{groupTripsQuery.data?.pagination && groupTripsQuery.data.pagination.totalPages > 1 && (
									<div className={styles.paginationWrapper}>
										<Pagination
											records={groupTripsQuery.data.pagination.total}
											current={groupTripsPage}
											onChange={setGroupTripsPage}
											delta={2}
											fixed={1}
											limit={groupTripsQuery.data.pagination.perPage}
										/>
									</div>
								)}
							</>
						) : (
							<div className={styles.noDataWrapper} role="status">
								<p>No missing CO₂ data for this group.</p>
							</div>
						)}
					</div>
				)}
			</Modal>
		);
	}

	return (
		<Modal name="travelerDataCompliance" className={styles.travelerDataComplianceModalWrapper}>
			{travelerItinerariesQuery.isError && !displayBookerData ? (
				<div className={styles.errorWrapper}>
					<p>We couldn’t load these trips. Try again in a moment.</p>
				</div>
			) : !displayBookerData ? (
				<div className={styles.spinnerWrapper}>
					<Spinner absolute />
				</div>
			) : (
				<div className={styles.bookerPreviewContent}>
					{bookerPreviewData?.returnToGroup && (
						<button type="button" className={styles.backButton} onClick={backToGroupPreview}>
							← Back
						</button>
					)}
					{travelerItinerariesQuery.isFetching && displayBookerData && (
						<div className={styles.fetchingOverlay}>
							<Spinner absolute className={styles.fetchingSpinner} />
						</div>
					)}
					<FlexBlock
						alignItems={{
							[breakpoints.zero]: 'flex-start',
							[breakpoints.tablet]: 'center',
						}}
						justifyContent="space-between"
						flexDirection={{
							[breakpoints.zero]: 'column',
							[breakpoints.tablet]: 'row',
						}}
						rowGap="20px"
						className={styles.nameDesc}
					>
						<FlexBlock alignItems="center">
							<span className={styles.initials}>{getInitials(displayBookerData?.user?.name || '')}</span>
							<FlexBlock flexDirection="column" justifyContent="flex-start">
								<p className={styles.name}>Trips arranged by {displayBookerData?.user?.name}</p>
								{!!getBookerContactDetails(displayBookerData?.user).length && (
									<p className={styles.contactDetails}>
										{getBookerContactDetails(displayBookerData?.user).join(' · ')}
									</p>
								)}
							</FlexBlock>
						</FlexBlock>
					</FlexBlock>

					<FlexBlock columnGap="22px" alignItems="center" className={styles.totalNums}>
						<FlexBlock columnGap="10px" alignItems="center">
							<p>Total trips</p>
							<span>{displayBookerData?.totals?.totalItinerariesCount}</span>
						</FlexBlock>
						<FlexBlock columnGap="10px" alignItems="center">
							<p>Need details</p>
							<span>{displayBookerData?.totals?.nonCompliantItinerariesCount}</span>
						</FlexBlock>
					</FlexBlock>

					{getCurrentPageItems().length ? (
						<div className={styles.tableScrollWrapper}>
							<table>
								<thead>
									<tr>
										<th>Trip</th>
										<th>
											<FlexBlock alignItems="center" columnGap="2px" className={styles.filter}>
												Travel arranger{' '}
												<div className={styles.tooltip}>
													<Tooltip text={bookerTooltipText} />
												</div>
											</FlexBlock>
										</th>
										<th>
											<FlexBlock alignItems="center" columnGap="2px" className={styles.filter}>
												Traveler{' '}
												<div className={styles.tooltip}>
													<Tooltip text={travelerTooltipText} />
												</div>
											</FlexBlock>
										</th>
										<th>Booked on</th>
										<th className={styles.alignRightHeader}>
											<FlexBlock
												alignItems="center"
												justifyContent="flex-end"
												columnGap="2px"
												className={styles.filter}
											>
												Missing details
												<div className={styles.tooltip}>
													<Tooltip text={missingDetailsTooltipText} />
												</div>
											</FlexBlock>
										</th>
									</tr>
								</thead>
								<tbody>
									{getCurrentPageItems().map((itinerary, index) => (
										<tr
											key={`${itinerary.id}-${itinerary.bookerId}-${itinerary.travelerId}-${index}`}
										>
											<td>
												<FlexBlock
													alignItems="center"
													columnGap="4px"
													className={styles.destination}
												>
													<FlexBlock flexDirection="column" rowGap="2px">
														<p className={styles.city}>
															<TripNameWithTooltip
																name={itinerary.tripName}
																providerRef={itinerary.providerRef}
															/>
														</p>
														<p className={styles.date}>
															{`${moment(itinerary.startDate).format('DD.MM.YY')} – ${moment(itinerary.endDate).format('DD.MM.YY')}`}
														</p>
													</FlexBlock>
												</FlexBlock>
											</td>
											<td>{itinerary.bookerName || displayBookerData?.user?.name || '–'}</td>
											<td>{getTravelerName(itinerary.travelerName)}</td>
											<td>{moment(itinerary.bookedOn).format('DD.MM.YY')}</td>
											<td>
												<span className={styles.complianceFlag}>
													{itinerary.complianceGapType === 'missing-hotel'
														? 'Accommodation'
														: 'Transport'}
												</span>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					) : (
						<div className={styles.noDataWrapper} role="status">
							<p>No missing CO₂ data for this travel arranger.</p>
						</div>
					)}

					{displayBookerData?.pagination && displayBookerData.pagination.totalPages > 1 && (
						<div className={styles.paginationWrapper}>
							<Pagination
								records={displayBookerData?.pagination?.total || 0}
								current={pagination.page}
								onChange={handlePagination}
								delta={2}
								fixed={1}
								limit={pagination.perPage}
							/>
						</div>
					)}
				</div>
			)}
		</Modal>
	);
};

export default TravelerDataComplianceModal;
